<script>
const ROLE_BADGE = {
  SuperAdmin: 'list-badge--superadmin',
  Admin: 'list-badge--admin',
  Professional: 'list-badge--professional',
};

export default {
  name: 'UserTable',
  props: {
    users: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    hasFilters: { type: Boolean, default: false },
    /**
     * Un mot de passe temporaire part en clair dans une boîte aux lettres et
     * sa forme est connue d'avance : seul un super administrateur en envoie,
     * et l'API le refuse aux autres. Le bouton suit cette règle plutôt que de
     * promettre une action qui reviendrait en 403.
     */
    canSendTemporaryPassword: { type: Boolean, default: false },
    /** Le compte de celui qui regarde : il ne se l'envoie pas à lui-même. */
    currentUserId: { type: Number, default: null },
  },
  emits: ['edit', 'delete', 'temporary-password', 'clear-filters'],
  methods: {
    fullName(user) {
      return [user.firstName, user.lastName].filter(Boolean).join(' ') || '-';
    },

    roleOf(user) {
      return user.roleName || user.role?.name || 'N/A';
    },

    badgeClass(user) {
      return ROLE_BADGE[this.roleOf(user)] || 'list-badge--neutral';
    },

    /**
     * Un compte porte son code d'activation tant que le lien reçu n'a pas été
     * ouvert. La colonne le dit: filtrer sur « activation en attente » sans
     * pouvoir le lire sur la ligne laisserait douter du résultat.
     */
    pending(user) {
      return user.accountState === 'pending';
    },

    /**
     * Le compte porte un mot de passe temporaire qu'il n'a pas encore
     * remplacé. La liste est l'écran depuis lequel il part : sans repère, rien
     * ne distingue le compte qu'on vient de traiter des autres.
     */
    temporary(user) {
      return Boolean(user.mustChangePassword);
    },

    /**
     * S'envoyer un mot de passe temporaire à soi-même ferme la plateforme à
     * l'instant du clic : plus une seule route ouverte, y compris celle-ci.
     * L'API le refuse; le bouton ne le propose pas.
     */
    mayReceiveTemporaryPassword(user) {
      return this.canSendTemporaryPassword && user.id !== this.currentUserId;
    },
  },
};
</script>

<template>
  <div>
    <p v-if="error" class="list-card__error" role="alert">{{ error }}</p>

    <p v-if="loading" class="list-card__state">Chargement des utilisateurs…</p>

    <div v-else-if="users.length" class="list-card__table-wrap">
      <table class="list-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Nom</th>
            <th>Email</th>
            <th>Téléphone</th>
            <th>Rôle</th>
            <th>État</th>
            <th>Enquêtes</th>
            <th class="list-table__actions-head">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td data-label="ID">{{ user.id }}</td>
            <td
              class="list-table__name"
              data-label="Nom"
              @click="$emit('edit', user.id)"
              @keyup.enter="$emit('edit', user.id)"
            >
              {{ fullName(user) }}
            </td>
            <td data-label="Email">{{ user.email }}</td>
            <td data-label="Téléphone">{{ user.phone || '-' }}</td>
            <td data-label="Rôle">
              <span class="list-badge" :class="badgeClass(user)">
                {{ roleOf(user) }}
              </span>
            </td>
            <td data-label="État">
              <span
                v-if="temporary(user)"
                class="list-badge list-badge--draft"
                data-testid="user-temporary-badge"
              >
                Mot de passe temporaire
              </span>
              <span
                v-else
                class="list-badge"
                :class="
                  pending(user) ? 'list-badge--draft' : 'list-badge--submitted'
                "
              >
                {{ pending(user) ? 'Activation en attente' : 'Actif' }}
              </span>
            </td>
            <td data-label="Enquêtes">{{ user.submissionsCount ?? 0 }}</td>
            <td>
              <div class="list-table__actions">
                <button
                  type="button"
                  class="row-action"
                  title="Modifier"
                  @click="$emit('edit', user.id)"
                >
                  <i class="ri-pencil-line"></i>
                  Modifier
                </button>
                <button
                  v-if="mayReceiveTemporaryPassword(user)"
                  type="button"
                  class="row-action"
                  title="Envoyer un mot de passe temporaire"
                  :data-testid="`user-temporary-password-${user.id}`"
                  @click="$emit('temporary-password', user)"
                >
                  <i class="ri-key-2-line"></i>
                </button>
                <button
                  type="button"
                  class="row-action row-action--danger"
                  title="Supprimer"
                  @click="$emit('delete', user)"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="list-card__empty">
      <p class="list-card__empty-text">
        {{
          hasFilters
            ? 'Aucun utilisateur ne correspond à ces filtres.'
            : 'Aucun utilisateur pour le moment.'
        }}
      </p>
      <button
        v-if="hasFilters"
        type="button"
        class="list-card__empty-action"
        @click="$emit('clear-filters')"
      >
        Effacer les filtres
      </button>
    </div>
  </div>
</template>
